import {
    ChevronSmallRightIcon,
    PinFilledIcon,
} from "@wags/ui/product-icons.tsx";
import { cn } from "@wags/ui/ui-utils";
import { useLocationLabel } from "@/features/viewer-location";
import type { FindLocationOrigin } from "../routes/find-location-origin";

export function FindLocationChip({
    origin,
    onChange,
}: {
    origin: FindLocationOrigin;
    onChange: () => void;
}) {
    const { locationLabel } = useLocationLabel({
        lat: origin.lat,
        lng: origin.lng,
    });
    const guessed = origin.status === "guessed";
    const visibleLabel = `Near ${locationLabel || "you"}${guessed ? "?" : ""}`;
    const accessibleLabel = guessed
        ? `${visibleLabel} Estimated from your network. Change location`
        : `${visibleLabel}. Change location`;

    return (
        <button
            type="button"
            aria-label={accessibleLabel}
            aria-haspopup="dialog"
            data-location-status={origin.status}
            onClick={onChange}
            className={cn(
                "group relative inline-flex h-9 max-w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-full border pl-3 pr-2.5 text-body-small outline-none transition-[background-color,border-color,transform] duration-150 ease-out before:absolute before:-inset-1 [@media(hover:hover)]:hover:bg-doberman-black/4 active:scale-97 motion-reduce:active:scale-100 focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-true-white",
                guessed
                    ? "border-transparent text-doberman-black/70"
                    : "border-doberman-black/12 bg-true-white text-doberman-black [@media(hover:hover)]:hover:border-doberman-black/20",
            )}
        >
            {guessed ? (
                <svg
                    aria-hidden="true"
                    // oxlint-disable-next-line shadcn/no-arbitrary-values -- the outline spans the button's 1px border box, so it matches the confirmed border
                    className="pointer-events-none absolute -inset-px size-[calc(100%+2px)] overflow-visible text-doberman-black/30 transition-colors duration-150 ease-out [@media(hover:hover)]:group-hover:text-doberman-black/45"
                >
                    <rect
                        x="0.5"
                        y="0.5"
                        // oxlint-disable-next-line shadcn/no-arbitrary-values -- a 1px stroke inset by half its width stays inside the border box
                        className="h-[calc(100%-1px)] w-[calc(100%-1px)]"
                        rx="17.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeDasharray="8 6"
                    />
                </svg>
            ) : null}
            <PinFilledIcon
                className="size-3.5 shrink-0 text-doberman-black/60"
                aria-hidden="true"
            />
            <span className="truncate">{visibleLabel}</span>
            <ChevronSmallRightIcon
                className="-ml-1 size-4 shrink-0 text-doberman-black/60 transition-transform duration-150 ease-out motion-reduce:transition-none [@media(hover:hover)]:group-hover:translate-x-0.5"
                aria-hidden="true"
            />
        </button>
    );
}
